/**
 * The client's Move key on the call bar (web): one tap moves the call to the
 * other signed-in device, and the key names it ("Move to Jane's iPhone",
 * vrs-mockups-7.html 13d). It shows only while another signed-in device can
 * take the call (companionDevices: server presence or nearby discovery).
 * With no such device there is nothing to move to, so it does not show.
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../base/icons/components/Icon';
import { IconDeviceTransfer } from '../../base/icons/svg';
import { getUserRole } from '../../base/user-role/functions';
import { hasPersistentAuthToken } from '../../vrs-auth/storage';
import { initiateHandoff } from '../actions';
/** Bar places that the labelled Move key takes on a wide bar (Toolbox counts them). */
export const MOVE_KEY_WIDE_PLACES = 4;
/** The labelled key's widest size: its places less one gap. A longer name ends in "…". */
const MOVE_KEY_MAX_WIDTH = (MOVE_KEY_WIDE_PLACES * 52) - 4;
/**
 * The web theme's palette. This file is also in the native type check
 * (device-handoff/index.ts exports it), which has no MUI palette
 * augmentation (base/ui/mui-theme.d.ts), so the type is named here.
 *
 * @param {Object} theme - The MUI theme.
 * @returns {IPalette}
 */
function paletteOf(theme) {
    return theme.palette;
}
const useStyles = makeStyles()(theme => {
    const palette = paletteOf(theme);
    return {
        move: {
            alignItems: 'center',
            background: 'transparent',
            border: `1px solid ${palette.action01}`,
            borderRadius: 8,
            boxSizing: 'border-box',
            color: palette.text01,
            cursor: 'pointer',
            display: 'inline-flex',
            flex: '0 1 auto',
            fontSize: 14,
            fontWeight: 600,
            gap: 8,
            height: 48,
            justifyContent: 'center',
            maxWidth: MOVE_KEY_MAX_WIDTH,
            minWidth: 48,
            padding: '0 14px',
            '&:focus-visible': {
                outline: `2px solid ${palette.focus01}`,
                outlineOffset: 2
            }
        },
        compact: {
            padding: 0,
            width: 48
        },
        label: {
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        }
    };
});
/**
 * The words on the key: the device by name, or "Move to another device" when
 * it has none (as moveButtonLabel on native).
 *
 * @param {CompanionDevice} device - The other signed-in device.
 * @param {Function} t - The translate function.
 * @returns {string}
 */
export function moveKeyLabel(device, t) {
    const name = typeof device.name === 'string' ? device.name.trim() : '';
    return name ? t('toolbar.moveTo', { name }) : t('toolbar.moveToAnotherDevice');
}
const HandoffBanner = ({ compact = false }) => {
    const { classes, cx, theme } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const companionDevices = useSelector((state) => state['features/device-handoff']?.companionDevices || []);
    const roomRole = useSelector((state) => state['features/base/jwt']?.user?.role);
    const handoffInProgress = useSelector((state) => state['features/device-handoff']?.handoffInProgress || false);
    const device = companionDevices[0];
    const handleMove = useCallback(() => {
        if (device) {
            dispatch(initiateHandoff(device));
        }
    }, [device, dispatch]);
    if (!device || !hasPersistentAuthToken() || getUserRole() !== 'client'
        || roomRole === 'guest' || roomRole === 'observer' || handoffInProgress) {
        return null;
    }
    const label = moveKeyLabel(device, t);
    return (React.createElement("div", null,
        React.createElement("button", { "aria-label": label, className: cx(classes.move, compact && classes.compact), onClick: handleMove, title: label, type: 'button' },
            React.createElement(Icon, { color: paletteOf(theme).icon01, size: 20, src: IconDeviceTransfer }),
            !compact && React.createElement("span", { className: classes.label }, label))));
};
export default HandoffBanner;
